import React from "react";

import type { LaneRendererProps } from "../lane.types";
import AudioAnalysisLaneRenderer from "../product/AudioAnalysisLaneRenderer";
import AudioPrimaryLaneRenderer from "../product/AudioPrimaryLaneRenderer";
import CapsuleLaneRenderer from "./CapsuleLaneRenderer";
import EventLaneRenderer from "./EventLaneRenderer";
import NpcLogicLaneRenderer from "../product/NpcLogicLaneRenderer";
import OverlayLaneRenderer from "./OverlayLaneRenderer";
import ProjectionContinuityLaneRenderer from "../product/ProjectionContinuityLaneRenderer";
import ProjectionEmotionLaneRenderer from "../product/ProjectionEmotionLaneRenderer";
import ProjectionHapticLaneRenderer from "../product/ProjectionHapticLaneRenderer";
import SegmentLaneRenderer from "./SegmentLaneRenderer";
import SignalLaneRenderer from "./SignalLaneRenderer";
import VideoPrimaryLaneRenderer from "../product/VideoPrimaryLaneRenderer";

function MissingLaneRenderer({
  laneId,
  label,
  summary,
}: {
  laneId: string;
  label: string;
  summary: string;
}): JSX.Element {
  return (
    <div
      data-lane-product={label.toLowerCase().replace(/\s+/g, "_")}
      data-lane-id={laneId}
      className="grid min-h-[56px] place-items-center rounded-2xl border border-amber-300/22 bg-[linear-gradient(135deg,rgba(251,191,36,0.12),rgba(2,6,23,0.16))] px-3 py-3 text-center shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]"
    >
      <div className="max-w-[30rem]">
        <div className="inline-flex rounded-full border border-amber-300/30 bg-amber-200 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.16em] text-amber-950 shadow-[0_0_0_1px_rgba(252,211,77,0.22),0_0_16px_rgba(251,191,36,0.16)]">
          {label}
        </div>
        <div className="mt-2 text-[11px] font-semibold text-amber-50">Renderer path is intentionally explicit here.</div>
        <div className="mt-1 text-[10px] text-amber-50/78">{summary}</div>
      </div>
    </div>
  );
}

export default function LaneRendererHost(props: LaneRendererProps): JSX.Element {
  const { lane } = props;

  switch (lane.productType) {
    case "video_primary":
      return <VideoPrimaryLaneRenderer {...props} />;
    case "audio_primary":
      return <AudioPrimaryLaneRenderer {...props} />;
    case "audio_analysis":
      return <AudioAnalysisLaneRenderer {...props} />;
    case "npc_logic":
      return <NpcLogicLaneRenderer {...props} />;
    case "projection_haptic":
      return <ProjectionHapticLaneRenderer {...props} />;
    case "projection_continuity":
      return <ProjectionContinuityLaneRenderer {...props} />;
    case "projection_emotion":
      return <ProjectionEmotionLaneRenderer {...props} />;
    case "capsule_temporal":
      return <CapsuleLaneRenderer {...props} />;
    default:
      return renderByLaneClass(props);
  }
}

function renderByLaneClass(props: LaneRendererProps): JSX.Element {
  const { lane } = props;

  switch (lane.class) {
    case "segment":
      return <SegmentLaneRenderer {...props} />;
    case "signal":
      return <SignalLaneRenderer {...props} />;
    case "event":
      return <EventLaneRenderer {...props} />;
    case "overlay":
      return <OverlayLaneRenderer {...props} />;
    case "capsule":
      return <CapsuleLaneRenderer {...props} />;
    default:
      return (
        <MissingLaneRenderer
          laneId="unknown-lane"
          label="Unknown Lane"
          summary="No renderer is registered for this lane shape."
        />
      );
  }
}